import React, { useContext, useState } from 'react'
import { Grid, GridCell, TabMenu, TabMenuButton, Text, View } from 'oio-react'
import { Helmet } from 'react-helmet'
import { Link, useRouteMatch } from 'react-router-dom'
import { LAYOUT_BOX } from 'config/constants/ui'
import { adminManageContentTypesUrl, initiativeUrl } from 'config/constants/urls'
import { useOrganization } from 'src/core/graphql/hooks'
import InitiativeList from 'src/sites/kits/Initiative/components/List'
import InitiativeMoreButton from 'src/sites/kits/Initiative/components/MoreButton'
import ObjectCreateControl from 'src/sites/kits/Object/components/CreateControl'
import ObjectStatusBanner from 'src/sites/kits/Object/components/StatusBanner'
import PeopleAsideBlock from 'src/sites/kits/People/components/AsideBlock'
import { EmptyContentBlock, LayoutContainer, LayoutContainerInner, Title, Titlebar }
   from 'src/sites/kits/UI'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { useGlobalState } from 'src/sites/state'

// List Items
import DiscussionListItemRow from 'src/sites/kits/Discussions/components/ListItemRow'
import InitiativeListItemDetailsCard from 'src/sites/kits/Initiative/components/ListItemDetailsCard'
import BlogListItemCard from 'src/sites/kits/Object/components/ListItemBlogCard'
import GroupListItemCard from 'src/sites/kits/Object/components/ListItemGroupCard'

const FILTER_BY_RECENT = 'recent'
const FILTER_BY_POPULARITY = 'popularity'
const FILTER_BY_FOLLOWING = 'following'

// Constants for each initiative Class
// Presumable these constants will eventually be saved to the database
// PR #951
const initiativeClassConstants = {
   discussion: {
      gridColumns: '1',
      gridSpacing: '0px',
      listItem: DiscussionListItemRow,
      listItemIconName: 'discussion',
      showGridMargin: false,
      showInitiativeListSortControl: true
   },
   group: {
      gridColumns: '1[a-b] 2[c-f]',
      gridSpacing: '15px[a-b] 40px[c-f]',
      listItem: GroupListItemCard,
      listItemIconName: 'discussion',
      showGridMargin: true,
      showInitiativeListSortControl: false
   },
   post: {
      gridColumns: '1',
      gridSpacing: '0px',
      listItem: BlogListItemCard,
      listItemIconName: 'post',
      showGridMargin: false,
      showInitiativeListSortControl: false
   },
   resource: {
      gridColumns: '1[a] 2[b] 3[c-d] 2[e-f]',
      gridSpacing: '15px[a-d] 40px[e-f]',
      listItem: InitiativeListItemDetailsCard,
      listItemIconName: 'resource',
      showGridMargin: true,
      showInitiativeListSortControl: false
   }
}

const DynamicListPageHome = () => {
   const match = useRouteMatch()
   const { theme } = useGlobalState()
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX
   const [initiativeListFilterMode, setInitiativeListFilterMode] = useState(FILTER_BY_RECENT)

   const { initiative: page } = useContext(InitiativeHierarchyContext)
   const { organization } = useOrganization()
   const dynamicPageType = organization.initiativeTypes.find(t => t.id === page.dynamicPageType.id)
   const initiativeTypeUIConstants = initiativeClassConstants[dynamicPageType.class] ||
      initiativeClassConstants.group

   const ListItemComponent = initiativeTypeUIConstants.listItem

   const initiativeListFilter = {
      archived: 'exclude',
      drafts: 'include',
      sortBy: 'dateLastUpdated',
      parentInitiativeId: page.id,
      limit: 12
   }

   if (initiativeListFilterMode === FILTER_BY_POPULARITY) {
      initiativeListFilter.sortBy = 'popularity'
   } else if (initiativeListFilterMode === FILTER_BY_FOLLOWING) {
      initiativeListFilter.userRelationshipFilter = {
         userId: 'me',
         hasElementType: 'subscribed'
      }
   }

   return (
      <LayoutContainer maxWidth="1200px">
         <LayoutContainerInner>
            <ObjectStatusBanner />
            <Helmet title={`${page.name} | ${organization.name}`} />
            <View
               display="flex"
               flexFlow={isBoxGL
                  ? 'column[a-d] row[e-f]'
                  : 'column[a-c] row[d-f]'
               }>
               <View
                  flex="1 1 auto"
                  height="100%"
                  minHeight="100vh"
                  borderRight={isBoxGL
                     ? `1px solid ${theme.tmpColumnBorderColor}[e-f]`
                     : `1px solid ${theme.tmpColumnBorderColor}[d-f]`}
                  paddingTop={isBoxGL && '10px[d] 30px[e-f]'}
                  paddingLeft={theme.tmpAppOuterPaddingHorizontal}
                  paddingRight={theme.tmpAppInnerPaddingHorizontal}>
                  <Titlebar size="lg">
                     <Title color={theme.tmpContentTitleColor}>
                        {page.name}
                     </Title>
                     <View display="flex">
                        <ObjectCreateControl
                           initiativeTypes={[dynamicPageType]}
                           parentInitiativeId={page.id}
                        />
                        {organization.currentUserCanEdit && (
                           <View display="block" width="10px" height="30px" />
                        )}
                        <InitiativeMoreButton
                           buttonRounded
                           buttonSize="sm"
                           buttonWidth="30px"
                           editUrl={`${match.url}/-/edit`}
                           popoverAnchorOriginVertical="top"
                           showInitiativeName
                        />
                     </View>
                  </Titlebar>
                  <View
                     paddingTop={initiativeTypeUIConstants.showGridMargin && theme.tmpSpacingTop}>
                     {initiativeTypeUIConstants.showInitiativeListSortControl && (
                        <TabMenu
                           buttonPaddingHorizontal="0px"
                           buttonSpacing="20px"
                           buttonSize="xl"
                           buttonTextSize="2"
                           highlightColor="#222">
                           <TabMenuButton
                              isActive={initiativeListFilterMode === FILTER_BY_RECENT}
                              onClick={() => setInitiativeListFilterMode(FILTER_BY_RECENT)}
                              name="Most Recent"
                           />
                           <TabMenuButton
                              isActive={initiativeListFilterMode === FILTER_BY_POPULARITY}
                              onClick={() => setInitiativeListFilterMode(FILTER_BY_POPULARITY)}
                              name="Most Popular"
                           />
                           <TabMenuButton
                              isActive={initiativeListFilterMode === FILTER_BY_FOLLOWING}
                              onClick={() => setInitiativeListFilterMode(FILTER_BY_FOLLOWING)}
                              name="Following"
                           />
                        </TabMenu>
                     )}
                     <Grid
                        columns={initiativeTypeUIConstants.gridColumns}
                        spacing={initiativeTypeUIConstants.gridSpacing}>
                        {!dynamicPageType.enabled && (
                           <GridCell colspan={initiativeTypeUIConstants.gridColumns}>
                              <EmptyContentBlock
                                 height="90px"
                                 message={(
                                    <View>
                                       {`${dynamicPageType.namePlural} have been disabled by the Admins of ${organization.name}.`}
                                       <br />
                                       <Link to={adminManageContentTypesUrl}>
                                          <Text
                                             weight="medium"
                                             style={{ display: 'inline' }}
                                             size={theme.tmpSubBodyTextSize}
                                             color={theme.tmpContentTextColor}>
                                             <u>Click here</u>
                                          </Text>
                                       </Link>
                                       &nbsp;to re-enable them.
                                    </View>
                                 )}
                              />
                           </GridCell>
                        )}
                        {dynamicPageType.enabled && (
                           <InitiativeList
                              emptyContent={(
                                 <GridCell colspan={initiativeTypeUIConstants.gridColumns}>
                                    <EmptyContentBlock
                                       height="90px"
                                       message="There is currently no content to show"
                                    />
                                 </GridCell>
                              )}
                              filter={initiativeListFilter}>
                              {initiativeList => initiativeList.items.map((initiative, index) => {
                                 const numMembers = initiative.roles.find(r => r.type === 'primary')?.numActiveUsers

                                 return (
                                    <GridCell key={initiative.id}>
                                       <ListItemComponent
                                          bodyTextColor={theme.tmpNavTextColor}
                                          bodyTextSize="2.5"
                                          borderStyle={`1px solid ${theme.tmpContentLineColor}`}
                                          coverMedia={initiative.coverMedia}
                                          coverUrl={initiative.coverMedia?.file.thumbnailUrlW480}
                                          iconName={initiativeTypeUIConstants.listItemIconName}
                                          linkTo={initiativeUrl(initiative)}
                                          meta={[initiative.dateAdded]}
                                          name={initiative.name}
                                          numThreadEntries={initiative.thread.numEntries}
                                          numViews={initiative.numViews}
                                          privacy={initiative.privacy}
                                          publishDate={initiative.publishDate}
                                          subtitle={initiative.subtitle}
                                          summary={initiative.body.summary}
                                          surveyEnabled={initiative.appsSurveyEnabled}
                                          textColor={theme.tmpTitleColor}

                                          // Discussion props
                                          addedBy={initiative.addedBy.fullName}
                                          archived={initiative.archived}
                                          authorBadges={initiative.addedBy.badges}
                                          dateLastUpdated={initiative.dateLastUpdated}
                                          discussionFormat={initiative.discussionFormat}
                                          discussionIsClosed={initiative.discussionStatus?.closed}
                                          hasUnreadEntries={initiative.thread.hasUnreadEntries}
                                          pinned={initiative.pinned}
                                          typeSlug={initiative.type.slug}

                                          // Group props
                                          dateAdded={initiative.dateAdded}
                                          members={initiative.members}
                                          numMembers={numMembers}
                                       />
                                    </GridCell>
                                 )
                              })}
                           </InitiativeList>
                        )}
                     </Grid>
                  </View>
               </View>
               <View
                  display={isBoxGL ? 'none[a-d] block[e-f]' : 'none[a-c] block[d-f]'}
                  flex="0 0 auto"
                  width={isBoxGL
                     ? '100%[a-d] 380px[e-f]'
                     : '100%[a-c] 360px[d-f]'
                  }
                  paddingLeft={theme.tmpAppInnerPaddingHorizontal}
                  paddingTop={isBoxGL && '10px[d] 30px[e-f]'}
                  marginTop={theme.tmpSpacingTop}
                  height="100%"
                  minHeight="100vh">
                  <PeopleAsideBlock />
               </View>
            </View>
         </LayoutContainerInner>
      </LayoutContainer>
   )
}

export default DynamicListPageHome
